import styled from '@emotion/styled';
import { Empty, Radio, Table, Tooltip, notification } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FiRefreshCcw } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import { TitreEntity } from '../../../../../entities/titre.entity';
import { PRIMARY, YELLOW } from '../../../../../shared/colors';
import { formatNum } from '../../../../../shared/constants';
import type { OrderPrefill } from '../../../pages/dashboard';
import {
  getTitresAchat,
  getTitresTotalTransact,
  getTitresVente,
  getUserTitres,
} from '../../../network';

const BID_COLOR = '#008f4f';
const ASK_COLOR = '#d92d20';

const Container = styled.div`
  height: 100%;
  box-sizing: border-box;
  padding: 8px;
  color: var(--mt-text);
  overflow-x: hidden;
  overflow-y: auto;

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: white !important;
    font-family: Poppins-B;
    font-size: 11px;
    font-weight: normal;
    text-transform: uppercase;
  }

  .monitor-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
  }

  .monitor-title {
    display: grid;
    gap: 2px;
  }

  .monitor-title span {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .monitor-title strong {
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 16px;
  }

  .monitor-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .refresh-icon {
    display: inline-flex;
    width: 28px;
    height: 28px;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--mt-border);
    border-radius: 999px;
    background-color: var(--mt-surface);
    cursor: pointer;
  }

  .offer-summary {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 8px;
  }

  .metric-card {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background-color: var(--mt-card);
    padding: 8px 10px;
  }

  .metric-card span {
    display: block;
    color: var(--mt-muted);
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .metric-card strong {
    display: block;
    margin-top: 3px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 15px;
  }

  .metric-card.bid strong {
    color: ${BID_COLOR};
  }

  .metric-card.ask strong {
    color: ${ASK_COLOR};
  }

  .offer-tabs {
    margin-bottom: 8px;
  }

  .offer-tabs .ant-radio-button-wrapper {
    height: 30px;
    padding: 0 14px;
    border-color: var(--mt-border);
    background-color: var(--mt-card);
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 12px;
  }

  .offer-tabs .ant-radio-button-wrapper-checked {
    border-color: var(--mt-accent) !important;
    background-color: var(--mt-accent) !important;
    color: #fff !important;
    box-shadow: none !important;
  }

  .table-shell {
    min-height: 0;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background-color: var(--mt-card);
    overflow: hidden;
  }

  .code-cell {
    max-width: 138px;
    overflow: hidden;
    color: var(--mt-text);
    font-family: Poppins-B;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .instrument-badge {
    display: inline-flex;
    align-items: center;
    border: 1px solid rgba(255, 176, 0, 0.28);
    border-radius: 999px;
    padding: 2px 7px;
    color: var(--mt-warning);
    background-color: rgba(255, 176, 0, 0.1);
    font-family: Poppins-B;
    font-size: 10px;
  }

  .price-pair {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-family: Poppins-B;
    white-space: nowrap;
  }

  .price-bid {
    color: ${BID_COLOR};
  }

  .price-ask {
    color: ${ASK_COLOR};
  }

  .price-separator {
    color: var(--mt-muted);
    font-family: Poppins-R;
  }

  .ant-table-wrapper,
  .ant-table,
  .ant-table-container,
  .ant-table-content {
    background: transparent !important;
  }

  .ant-table-container {
    border: 0;
    border-radius: 0;
  }

  .ant-table-tbody > tr > td {
    background: inherit !important;
  }

  .ant-empty-description {
    color: var(--mt-muted) !important;
  }

  &::-webkit-scrollbar {
    width: 0.5vw;
  }

  &::-webkit-scrollbar-thumb {
    background-image: linear-gradient(to bottom, #eceff1, #eceff1);
    border-radius: 10px;
  }
`;

export const Offres = ({
  getTitre,
  titresData,
}: {
  getTitre: (
    b: TitreEntity,
    ope: '1' | '2',
    detUrl?: string,
    prefill?: OrderPrefill,
  ) => void;
  titresData: TitreEntity[];
}) => {
  const [value, setValue] = useState('1');
  const [titresAchat, setTitresAchat] = useState([]);
  const [titresVente, setTitresVente] = useState([]);
  const [titresTotal, setTitresTotal] = useState([]);
  const connectedUser = useSelector((state: any) => state.userReducer).user;
  const [mesTitres, setMesTitres] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = () => {
    setIsLoading(true);
    getTitresAchat().then(({ data }) => {
      setTitresAchat(data);
      setIsLoading(false);
    });
    getTitresVente().then(({ data }) => {
      setTitresVente(data);
    });
    getTitresTotalTransact().then(({ data }) => {
      setTitresTotal(data);
    });
    getUserTitres(connectedUser.id).then(({ data }) => {
      setMesTitres(normalizeList(data));
    });
  };

  const getColumns = (val: string) => {
    if (val === '1') return TOTAL_COLUMNS;
    else if (val === '2') return VENTE_COLUMNS;
    else return ACHAT_COLUMNS;
  };

  const getContent = (val: string) => {
    if (val === '1') return titresTotal;
    else if (val === '2') return titresVente;
    else return titresAchat;
  };

  const getPrefill = (row: any, side: 'buy' | 'sell'): OrderPrefill => ({
    prix: Number(side === 'buy' ? row.ask || row.prix : row.bid || row.prix) || 0,
    qte: getRowQuantity(row),
  });

  const offeredVolume = sumQuantity(titresVente);
  const requestedVolume = sumQuantity(titresAchat);

  return (
    <Container>
      <div
        className='monitor-toolbar'
      >
        <div className='monitor-title'>
          <span>Order book</span>
          <strong>Carnet des offres</strong>
        </div>
        <div className='monitor-actions'>
          <PulseLoader size={5} margin={2} color={YELLOW} loading={isLoading} />
          <Tooltip title='Rafraichir' placement='leftTop' color={YELLOW}>
            <span className='refresh-icon' onClick={fetchData}>
              <FiRefreshCcw />
            </span>
          </Tooltip>
        </div>
      </div>

      <div className='offer-summary'>
        <div className='metric-card'>
          <span>Titres agrégés</span>
          <strong>{titresTotal.length}</strong>
        </div>
        <div className='metric-card ask'>
          <span>Volume offert</span>
          <strong>{formatNum(offeredVolume)}</strong>
        </div>
        <div className='metric-card bid'>
          <span>Volume demandé</span>
          <strong>{formatNum(requestedVolume)}</strong>
        </div>
      </div>

      <Radio.Group
        className='offer-tabs'
        value={value}
        onChange={(e) => setValue(e.target.value)}
      >
        <Radio.Button value='1'>
          Titres total
        </Radio.Button>
        <Radio.Button value='2'>
          Volume offert
        </Radio.Button>
        <Radio.Button value='3'>
          Volume demandé
        </Radio.Button>
      </Radio.Group>

      <div className='table-shell'>
        <Table
          columns={getColumns(value)}
          dataSource={getContent(value)}
          rowSelection={
            value !== '1'
              ? {
                  type: 'radio',
                  onChange: (selectedRowKeys, selectedRows) => {
                    const selectedRow = selectedRows[0];
                    if (!selectedRow) return;

                    if (value === '2') {
                      getTitre(
                        selectedRow,
                        '1',
                        undefined,
                        getPrefill(selectedRow, 'buy'),
                      );
                    } else if (value === '3') {
                      const found = normalizeList(mesTitres).some(
                        (det) =>
                          selectedRow.id === det.titre?.id ||
                          selectedRow.id === det.titre ||
                          selectedRow.url === det.titre,
                      );
                      if (found) {
                        getTitre(
                          selectedRow,
                          '2',
                          undefined,
                          getPrefill(selectedRow, 'sell'),
                        );
                      } else {
                        notification.info({
                          message: 'Attention',
                          description: 'Vous ne possédez pas ce titre !!!',
                        });
                      }
                    }
                  },
                }
              : undefined
          }
          rowClassName={(row, index) =>
            index % 2 === 0 ? 'even-row' : 'odd-row'
          }
          rowKey='id'
          size='small'
          style={{ zoom: 0.8 }}
          scroll={{ x: 900 }}
          pagination={false}
          showSorterTooltip={false}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description='Aucune offre disponible'
              />
            ),
          }}
        />
      </div>
    </Container>
  );
};

const formatMaturity = (value: string, row: TitreEntity) =>
  row.instrument === 'ACTION' || !value || value === 'Invalid date'
    ? '—'
    : moment(value).format('DD/MM/YYYY');

const renderCode = (row: TitreEntity) => (
  <div className='code-cell' title={row.code}>
    {row.code}
  </div>
);

const renderInstrument = (value: string) => (
  <span className='instrument-badge'>{value || '—'}</span>
);

const renderCoupon = (row: TitreEntity) =>
  row.instrument === 'ACTION' ? '—' : Number(row.tauxMarginal);

const getRowQuantity = (row: any) =>
  Number(row.quantite || row.qteRestant || row.qteOriginal || row.qte) || 0;

const normalizeList = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.titres)) return data.titres;
  return [];
};

const sumQuantity = (rows: any[]) =>
  rows.reduce((total, row) => total + getRowQuantity(row), 0);

const renderQuantity = (row: any) => {
  const quantity = getRowQuantity(row);
  return quantity ? formatNum(quantity) : '—';
};

const renderPrice = (value: number, side: 'bid' | 'ask') => (
  <span className={side === 'bid' ? 'price-bid' : 'price-ask'}>
    {value ? formatNum(value) : '—'}
  </span>
);

const renderPricePair = (row: any) => (
  <span className='price-pair'>
    {renderPrice(row.bid, 'bid')}
    <span className='price-separator'>/</span>
    {renderPrice(row.ask, 'ask')}
  </span>
);

const TOTAL_COLUMNS = [
  {
    title: 'Pays',
    dataIndex: 'pays',
    sorter: (a: TitreEntity, b: TitreEntity) => a.pays.localeCompare(b.pays),
  },
  {
    title: 'Type',
    dataIndex: 'instrument',
    sorter: (a: TitreEntity, b: TitreEntity) =>
      a.instrument.localeCompare(b.instrument),
    render: (cell: string) => renderInstrument(cell),
  },
  {
    title: 'CODE',
    dataIndex: 'code',
    sorter: (a: TitreEntity, b: TitreEntity) => a.code.localeCompare(b.code),
    render: (cell: any, row: TitreEntity) => renderCode(row),
  },
  {
    title: 'Date de maturation',
    dataIndex: 'dateMaturite',
    render: (cell: any, row: TitreEntity) => formatMaturity(cell, row),
    sorter: (a: TitreEntity, b: TitreEntity) =>
      a.dateMaturite.localeCompare(b.dateMaturite),
  },
  {
    title: 'Coupon',
    dataIndex: 'tauxMarginal',
    sorter: (a: TitreEntity, b: TitreEntity) => a.tauxMarginal - b.tauxMarginal,
    render: (cell: any, row: TitreEntity) => renderCoupon(row),
  },
  {
    title: 'Bid Px.\\Ask Px.',
    render: (cell: any, row: any) => renderPricePair(row),
  },
  {
    title: 'Qté Bid\\Ask',
    render: (cell: any, row: any) => (
      <span className='price-pair'>
        <span className='price-bid'>
          {row.bidQuantite ? formatNum(Number(row.bidQuantite)) : '—'}
        </span>
        <span className='price-separator'>/</span>
        <span className='price-ask'>
          {row.askQuantite ? formatNum(Number(row.askQuantite)) : '—'}
        </span>
      </span>
    ),
  },
];

const ACHAT_COLUMNS = [
  {
    title: 'Pays',
    dataIndex: 'pays',
    sorter: (a: TitreEntity, b: TitreEntity) => a.pays.localeCompare(b.pays),
  },
  {
    title: 'Type',
    dataIndex: 'instrument',
    sorter: (a: TitreEntity, b: TitreEntity) =>
      a.instrument.localeCompare(b.instrument),
    render: (cell: string) => renderInstrument(cell),
  },
  {
    title: 'CODE',
    dataIndex: 'code',
    sorter: (a: TitreEntity, b: TitreEntity) => a.code.localeCompare(b.code),
    render: (cell: any, row: TitreEntity) => renderCode(row),
  },
  {
    title: 'Qté',
    render: (cell: any, row: any) => renderQuantity(row),
    sorter: (a: any, b: any) => getRowQuantity(a) - getRowQuantity(b),
  },
  {
    title: 'Date de maturation',
    dataIndex: 'dateMaturite',
    render: (cell: any, row: TitreEntity) => formatMaturity(cell, row),
    sorter: (a: TitreEntity, b: TitreEntity) =>
      a.dateMaturite.localeCompare(b.dateMaturite),
  },
  {
    title: 'Coupon',
    dataIndex: 'tauxMarginal',
    sorter: (a: TitreEntity, b: TitreEntity) => a.tauxMarginal - b.tauxMarginal,
    render: (cell: any, row: TitreEntity) => renderCoupon(row),
  },
  {
    title: 'Bid Prix',
    render: (cell: any, row: any) => renderPrice(row.bid, 'bid'),
  },
  {
    title: 'Bid Yield',
    render: (cell: any, row: any) => renderPrice(row.bid, 'bid'),
  },
];

const VENTE_COLUMNS = [
  {
    title: 'Pays',
    dataIndex: 'pays',
    sorter: (a: TitreEntity, b: TitreEntity) => a.pays.localeCompare(b.pays),
  },
  {
    title: 'Type',
    dataIndex: 'instrument',
    sorter: (a: TitreEntity, b: TitreEntity) =>
      a.instrument.localeCompare(b.instrument),
    render: (cell: string) => renderInstrument(cell),
  },
  {
    title: 'CODE',
    dataIndex: 'code',
    sorter: (a: TitreEntity, b: TitreEntity) => a.code.localeCompare(b.code),
    render: (cell: any, row: TitreEntity) => renderCode(row),
  },
  {
    title: 'Qté',
    render: (cell: any, row: any) => renderQuantity(row),
    sorter: (a: any, b: any) => getRowQuantity(a) - getRowQuantity(b),
  },
  {
    title: 'Décote',
    dataIndex: 'decote',
    // sorter: (a: TitreEntity, b: TitreEntity) => a.decote,
  },
  {
    title: 'Date de maturation',
    dataIndex: 'dateMaturite',
    render: (cell: any, row: TitreEntity) => formatMaturity(cell, row),
    sorter: (a: TitreEntity, b: TitreEntity) =>
      a.dateMaturite.localeCompare(b.dateMaturite),
  },
  {
    title: 'Coupon',
    dataIndex: 'tauxMarginal',
    sorter: (a: TitreEntity, b: TitreEntity) => a.tauxMarginal - b.tauxMarginal,
    render: (cell: any, row: TitreEntity) => renderCoupon(row),
  },
  {
    title: 'Ask Prix',
    render: (cell: any, row: any) => renderPrice(row.ask, 'ask'),
  },
  {
    title: 'Ask Yield',
    render: (cell: any, row: any) => renderPrice(row.ask, 'ask'),
  },
];
